import React, { useEffect, useState, useContext } from "react";
import MainContext from "../Context/MainContext/MainContext";
import MUISkeleton from "@mui/material/Skeleton";
import MUIBox from "@mui/material/Box";
import MUIPaper from "@mui/material/Paper";
import MUITooltip from "@mui/material/Tooltip";
import MUITypography from "@mui/material/Typography";

import api from "../Seating/Security/api";
import Setting from "../Utility/Setting";
import Security from "../Utility/Security";
import User from "../Utility/Crud/User";
import Term from "../Utility/Crud/Term";

/**
 * @returns {string|null} The widget title shown in the caption row.
 */
function defaultGetTitle() {
  return null;
}

/*
 * @returns {string|null} The widget subtitle shown in the caption row.
 */
function defaultGetSubtitle() {
  return null;
}

/**
 * Renders the main body below the title row. Override by passing
 * `renderContent`.
 *
 * @param {object} widgetRenderProperties
 * @param {object|null} widgetRenderProperties.widgetData Loaded widget response data.
 * @param {boolean} widgetRenderProperties.isLoadingWidgetData Whether widget data is loading.
 * @param {boolean} widgetRenderProperties.isErrorWidgetData Whether widget data failed to load.
 * @returns {React.ReactNode|null}
 */
function defaultRenderContent(widgetRenderProperties) {
  return null;
}

/**
 * Renders the right-side full-bleed visual panel (graph, sparkline, colored
 * accent, icon, etc.). When this returns a non-null value the layout splits
 * into a left content column and a right visual column.
 *
 * @param {object} widgetRenderProperties
 * @param {object|null} widgetRenderProperties.widgetData Loaded widget response data.
 * @param {boolean} widgetRenderProperties.isLoadingWidgetData Whether widget data is loading.
 * @param {boolean} widgetRenderProperties.isErrorWidgetData Whether widget data failed to load.
 * @returns {React.ReactNode|null}
 */
function defaultRenderVisual(widgetRenderProperties) {
  return null;
}

/**
 * No-op default for the onClick handler so the prop is always callable.
 */
function defaultOnClick() {
  return null;
}

/**
 * @returns {boolean} Whether the widget is active.
 */
function defaultIsActive() {
  return false;
}

/**
 * @returns {function} The function that returns the widget record from context (`widgetsIndexed`) for this dashboard tile.
 */
function defaultGetWidgetId() {
  return null;
}

function defaultRenderTooltip(widgetRenderProperties) {
  return null;
}

/**
 * No-op default for {@link onWidgetRenderPropertiesChange}.
 *
 * @param {object} widgetRenderProperties
 */
function defaultOnWidgetRenderPropertiesChange(widgetRenderProperties) {
  return null;
}

/**
 * Shared shell for dashboard widgets: Paper container, caption title
 * row, optional header actions, content region, and an optional full-bleed
 * right-side visual panel.
 *
 * @param {object} props
 * @param {function} [props.getTitle] Returns the widget title string.
 * @param {function} [props.getSubtitle] Returns the widget subtitle string.
 * @param {function} [props.renderContent] Renders the main body below the caption title row when data loads successfully.
 * @param {function} [props.renderVisual] Renders the right-side full-bleed visual panel. The visual column has zero padding — content should fill
 * @param {function} [props.renderTooltip] Renders the tooltip content for the widget.
 * @param {function} [props.onClick] Called when the widget is clicked. When not the default no-op, pointer cursor and hover shadow apply even if `getHref` returns null.
 * @param {function} [props.isActive] Returns whether the widget should show the active visual treatment.
 * @param {function} [props.getWidget] The function that returns the widget record from context (`widgetsIndexed`) for this dashboard tile.
 * @param {boolean} [props.forceRefresh] Whether the widget data request should bypass cached data.
 * @param {boolean} [props.useCompactWidth] Whether the widget should remain at its 190px base width instead of growing.
 * @param {function} [props.onWidgetRenderPropertiesChange] Called after paint when loading state, error state, or loaded widget payload changes. Lets parents run effects (for example updating dashboard block configuration) without calling parent setters synchronously inside `renderContent`.
 * @returns {React.ReactElement}
 */
export default function Widget({
  getTitle = defaultGetTitle,
  getSubtitle = defaultGetSubtitle,
  renderContent = defaultRenderContent,
  renderVisual = defaultRenderVisual,
  renderTooltip = defaultRenderTooltip,
  onClick = defaultOnClick,
  isActive = defaultIsActive,
  getWidgetId = defaultGetWidgetId,
  forceRefresh = false,
  useCompactWidth = false,
  onWidgetRenderPropertiesChange = defaultOnWidgetRenderPropertiesChange,
}) {
  const mainContext = useContext(MainContext);
  const widgetIdentifier = getWidgetId() ?? null;

  const [widgetData, setWidgetData] = useState(null);
  const [isLoadingWidgetData, setIsLoadingWidgetData] = useState(true);
  const [isErrorWidgetData, setIsErrorWidgetData] = useState(false);
  const [prefilterRefreshCounter, setPrefilterRefreshCounter] = useState(0);

  const widgetRenderProperties = {
    widgetData: widgetData,
    isLoadingWidgetData: isLoadingWidgetData,
    isErrorWidgetData: isErrorWidgetData,
  };

  /**
   * Listen for changes to any of the queue prefilter settings in
   * localStorage. `Setting.set(...)` and `Setting.clear(...)` dispatch a
   * `setting.<key>` window event, so we subscribe to each prefilter key and
   * bump a counter to trigger a refetch. We also listen for the native
   * `storage` event so changes made in another tab refresh this widget too.
   */
  useEffect(() => {
    const prefilterSettingKeys = [
      "queue.prefilters.regionIds",
      "queue.prefilters.locationIds",
      "queue.prefilters.userIds",
      "queue.prefilters.marketingCampaignIds",
      "queue.prefilters.primaryProductTypeIds",
      "queue.prefilters.primaryInsuranceTypeIds",
    ];

    const handlePrefilterSettingChange = () => {
      setPrefilterRefreshCounter((previousCounter) => previousCounter + 1);
    };

    const handleCrossTabStorageChange = (storageEvent) => {
      if (prefilterSettingKeys.includes(storageEvent.key)) {
        handlePrefilterSettingChange();
      }
    };

    prefilterSettingKeys.forEach((prefilterSettingKey) => {
      window.addEventListener(
        `setting.${prefilterSettingKey}`,
        handlePrefilterSettingChange,
      );
    });
    window.addEventListener("storage", handleCrossTabStorageChange);

    return () => {
      prefilterSettingKeys.forEach((prefilterSettingKey) => {
        window.removeEventListener(
          `setting.${prefilterSettingKey}`,
          handlePrefilterSettingChange,
        );
      });
      window.removeEventListener("storage", handleCrossTabStorageChange);
    };
  }, []);

  useEffect(() => {
    if (!widgetIdentifier) {
      setIsLoadingWidgetData(false);
      setIsErrorWidgetData(false);
      setWidgetData(null);
      return;
    }

    setIsLoadingWidgetData(true);
    setIsErrorWidgetData(false);
    setWidgetData(null);

    setRegionTypeTermIdSetting();

    const widgetDataRequestObject = {
      dashboardWidgetId: widgetIdentifier,
      prefilters: getPrefilters(),
    };
    if (forceRefresh === true) {
      widgetDataRequestObject.forceRefresh = true;
    }

    api
      .send("POST", `/advancedDashboard/widgetData`, widgetDataRequestObject)
      .then((data) => {
        // console.log(data)
        setWidgetData(data);
      })
      .catch(() => {
        setIsErrorWidgetData(true);
      })
      .finally(() => {
        setIsLoadingWidgetData(false);
      });
  }, [widgetIdentifier, prefilterRefreshCounter, forceRefresh]);

  useEffect(() => {
    onWidgetRenderPropertiesChange({
      widgetData: widgetData,
      isLoadingWidgetData: isLoadingWidgetData,
      isErrorWidgetData: isErrorWidgetData,
    });
  }, [
    widgetData,
    isLoadingWidgetData,
    isErrorWidgetData,
    onWidgetRenderPropertiesChange,
  ]);

  /**
   * Get prefilters based on the settings.
   *
   * @param {number} id The ID to get prefilters for.
   * @returns {array}
   */
  function getPrefilters() {
    const prefilters = [];

    if (
      Security.hasPermission("queue.useRegionPrefilter") === false &&
      Security.hasPermission("queue.useLocationPrefilter") === false &&
      Security.hasPermission("queue.useUserPrefilter") === false &&
      Security.hasPermission("queue.useMarketingCampaignPrefilter") === false &&
      Security.hasPermission("queue.usePrimaryProductTypePrefilter") ===
        false &&
      Security.hasPermission("queue.usePrimaryInsuranceTypePrefilter") === false
    ) {
      return prefilters;
    }

    /**
     * For all of these prefilters, we default to using the user's chosen
     * setting (if one is set). If the user did not explicitly pick one,
     * then it defaults to all that they have access to. However, if the
     * user has access to all, then just don't set a filter at all to avoid
     * sending hundreds of IDs in a filter.
     */

    // Region
    const filteredRegions = User.getFilteredRegions(
      mainContext.currentUser,
      mainContext.userprofileLocationsIndexed,
      mainContext.regionsIndexed,
      mainContext.locationsIndexed,
    ).filter((region) => {
      if (Security.hasPermission("user.viewAllRegions") === true) {
        return true;
      } else {
        // Filter only regions with a type available to my prefilters
        return Object.values(mainContext.teamUsersIndexed).some((teamUser) => {
          if (teamUser.userId === mainContext.currentUser.id) {
            const team = mainContext.teamsIndexed[teamUser.teamId];
            if (team && team.departmentId) {
              const department =
                mainContext.departmentsIndexed[team.departmentId];
              return (
                department.jsonPrefilters?.regionTypeTermId ===
                region.regionTypeTermId
              );
            }
          }

          return false;
        });
      }
    });
    const filteredRegionIds = filteredRegions.map((region) => region.id);

    let prefilterRegionIds = null;
    if (Setting.get("queue.prefilters.regionIds")) {
      prefilterRegionIds = Setting.get("queue.prefilters.regionIds").filter(
        (regionId) => filteredRegionIds.includes(regionId),
      );
      if (prefilterRegionIds.length === 0) {
        prefilterRegionIds = filteredRegionIds;
      }
    } else {
      if (!Security.hasPermission("user.viewAllRegions")) {
        prefilterRegionIds = filteredRegionIds;
      }
    }

    // Location
    const filteredLocations = User.getFilteredLocations(
      mainContext.currentUser,
      mainContext.userprofileLocationsIndexed,
      mainContext.locationsIndexed,
    );
    const filteredLocationIds = filteredLocations.map(
      (location) => location.id,
    );

    let prefilterLocationIds = null;
    if (Setting.get("queue.prefilters.locationIds")) {
      prefilterLocationIds = Setting.get("queue.prefilters.locationIds").filter(
        (locationId) => filteredLocationIds.includes(locationId),
      );
      if (prefilterLocationIds.length === 0) {
        prefilterLocationIds = filteredLocationIds;
      }
    } else {
      if (!Security.hasPermission("user.viewAllLocations")) {
        prefilterLocationIds = filteredLocationIds;
      }
    }

    // User
    const filteredUsers = User.getFilteredUsers(
      mainContext.currentUser,
      mainContext.userprofileLocationsIndexed,
      mainContext.locationsIndexed,
      mainContext.usersIndexed,
      mainContext.salesPcrLinksIndexed,
    );
    const filteredUserIds = filteredUsers.map((user) => user.id);

    let prefilterUserIds = null;
    if (Setting.get("queue.prefilters.userIds")) {
      prefilterUserIds = Setting.get("queue.prefilters.userIds").filter(
        (userId) => filteredUserIds.includes(userId),
      );
      if (prefilterUserIds.length === 0) {
        prefilterUserIds = filteredUserIds;
      }
    } else {
      if (!Security.hasPermission("user.viewAllUsers")) {
        prefilterUserIds = filteredUserIds;
      }
    }

    // Marketing Campaign
    const filteredMarketingCampaignIds = Object.values(
      mainContext.marketingCampaignsIndexed,
    ).map((marketingCampaign) => marketingCampaign.id);

    let prefilterMarketingCampaignIds = null;
    if (
      Setting.get("queue.prefilters.marketingCampaignIds") &&
      Setting.get("queue.prefilters.marketingCampaignIds").length > 0
    ) {
      prefilterMarketingCampaignIds = Setting.get(
        "queue.prefilters.marketingCampaignIds",
      ).filter((marketingCampaignId) =>
        filteredMarketingCampaignIds.includes(marketingCampaignId),
      );
      if (prefilterMarketingCampaignIds.length === 0) {
        prefilterMarketingCampaignIds = filteredMarketingCampaignIds;
      }
    }

    // Set up the prefilter object.
    if (
      prefilterRegionIds &&
      Security.hasPermission("queue.useRegionPrefilter")
    ) {
      let key;
      if (Setting.get("queue.prefilters.regionTypeTermId") === 11) {
        // Sales
        key = "order_activity.sales_location.region_id";
      } else if (Setting.get("queue.prefilters.regionTypeTermId") === 12) {
        // Field Operations
        key = "order_activity.service_location.field_operations_region_id";
      } else {
        throw new Error(
          `Invalid queue.prefilters.regionTypeTermId: ${Setting.get("queue.prefilters.regionTypeTermId")}`,
        );
      }

      prefilters.push({
        Key: key,
        Value: prefilterRegionIds.map((value) => String(value)),
      });
    }

    if (
      prefilterLocationIds &&
      Security.hasPermission("queue.useLocationPrefilter")
    ) {
      let key;
      if (Setting.get("queue.prefilters.regionTypeTermId") === 11) {
        // Sales
        key = "order_activity.sales_location_id";
      } else if (Setting.get("queue.prefilters.regionTypeTermId") === 12) {
        // Field Operations
        key = "order_activity.service_location_id";
      } else {
        throw new Error(
          `Invalid queue.prefilters.regionTypeTermId: ${Setting.get("queue.prefilters.regionTypeTermId")}`,
        );
      }

      prefilters.push({
        Key: key,
        Value: prefilterLocationIds.map((value) => String(value)),
      });
    }

    if (prefilterUserIds && Security.hasPermission("queue.useUserPrefilter")) {
      prefilters.push({
        Key: "order_activity.activity.owner_id",
        // Key: (this.context.currentUser.isAtp) ? "order_activity.atp_id" : "order_activity.activity.owner_id",
        Value: prefilterUserIds.map((value) => String(value)),
      });
    }

    if (
      prefilterMarketingCampaignIds &&
      Security.hasPermission("queue.useMarketingCampaignPrefilter")
    ) {
      prefilters.push({
        Key: "order_activity.marketing_campaign_id",
        Value: prefilterMarketingCampaignIds.map((value) => String(value)),
      });
    }

    const filteredPrimaryProductTypeIds = Object.values(
      mainContext.productTypesIndexed,
    ).map((productType) => productType.id);
    const prefilterPrimaryProductTypeIds = (
      Setting.get("queue.prefilters.primaryProductTypeIds") || []
    ).filter((productTypeId) =>
      filteredPrimaryProductTypeIds.includes(productTypeId),
    );
    if (
      prefilterPrimaryProductTypeIds.length > 0 &&
      Security.hasPermission("queue.usePrimaryProductTypePrefilter")
    ) {
      prefilters.push({
        Key: "order_activity.order_activity_products._primary_product_type_id",
        Value: prefilterPrimaryProductTypeIds.map((value) => String(value)),
      });
    }

    const filteredPrimaryInsuranceTypeIds = Object.values(
      mainContext.insuranceTypesIndexed,
    ).map((insuranceType) => insuranceType.id);
    const prefilterPrimaryInsuranceTypeIds = (
      Setting.get("queue.prefilters.primaryInsuranceTypeIds") || []
    ).filter((insuranceTypeId) =>
      filteredPrimaryInsuranceTypeIds.includes(insuranceTypeId),
    );
    if (
      prefilterPrimaryInsuranceTypeIds.length > 0 &&
      Security.hasPermission("queue.usePrimaryInsuranceTypePrefilter")
    ) {
      prefilters.push({
        Key: "order_activity.insurance_type_id",
        Value: prefilterPrimaryInsuranceTypeIds.map((value) => String(value)),
      });
    }

    return prefilters;
  }

  function setRegionTypeTermIdSetting() {
    // Get a list of available region types.
    let availableRegionTypeTerms;
    if (Security.hasPermission("user.viewAllRegions") === true) {
      availableRegionTypeTerms = Term.sort(
        Term.read(mainContext.termsIndexed, [
          { key: "type", value: "region_type" },
        ]),
      );
    } else {
      const availableRegionTypeTermIds = {};
      Object.values(mainContext.teamUsersIndexed).some((teamUser) => {
        if (teamUser.userId === mainContext.currentUser.id) {
          const team = mainContext.teamsIndexed[teamUser.teamId];
          if (team && team.departmentId) {
            const department =
              mainContext.departmentsIndexed[team.departmentId];
            if (department.jsonPrefilters?.regionTypeTermId) {
              availableRegionTypeTermIds[
                department.jsonPrefilters.regionTypeTermId
              ] = true;
            }
          }
        }

        return false;
      });
      availableRegionTypeTerms = Term.sort(
        Term.read(mainContext.termsIndexed, [
          {
            key: "id",
            value: Object.keys(availableRegionTypeTermIds).map(Number),
          },
        ]),
      );
    }

    // Initialize this setting
    if (
      availableRegionTypeTerms.length > 0 &&
      (Setting.get("queue.prefilters.regionTypeTermId") === undefined ||
        !availableRegionTypeTerms.some(
          (term) =>
            term.id === Setting.get("queue.prefilters.regionTypeTermId"),
        ))
    ) {
      Setting.set(
        "queue.prefilters.regionTypeTermId",
        availableRegionTypeTerms[0].id,
      );
    }
  }

  const widgetPaperElement = (
    <MUIPaper
      onClick={onClick}
      elevation={isActive() ? 3 : 1}
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        textDecoration: "none",
        color: "inherit",
        ...(onClick !== defaultOnClick && {
          cursor: "pointer",
          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
          "&:hover": {
            boxShadow: (theme) =>
              theme.shadows[Math.min(3, theme.shadows.length - 1)],
          },
        }),
        ...(isActive() && {
          borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.action.selected
              : theme.palette.primary.light + "28",
        }),
        minWidth: 190,
        flex: useCompactWidth ? "0 0 190px" : "1 1 190px",
        maxWidth: useCompactWidth ? 190 : 280,
        minHeight: 100,
        px: 0.75,
        py: 0.5,
      }}
    >
      {isLoadingWidgetData === true ? (
        <MUIBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <MUITypography variant="body1">{getTitle()}</MUITypography>
          <MUISkeleton height={32} variant="rounded" width="100%" />
        </MUIBox>
      ) : isErrorWidgetData === true ? (
        <>
          <MUIBox sx={{ pb: 0.25 }}>
            <MUITypography variant="body1">{getTitle()}</MUITypography>
          </MUIBox>
          <MUITypography color="error" variant="body2">
            Could not load widget data
          </MUITypography>
        </>
      ) : (
        <>
          <MUIBox sx={{ pb: 0.25 }}>
            <MUITypography variant="body1">{getTitle()}</MUITypography>
          </MUIBox>

          {(() => {
            const renderedVisual = renderVisual(widgetRenderProperties);
            const subtitleText = getSubtitle(widgetRenderProperties);
            const hasVisual =
              renderedVisual !== null &&
              renderedVisual !== undefined &&
              renderedVisual !== false;
            const hasSubtitle =
              subtitleText !== null &&
              subtitleText !== undefined &&
              subtitleText !== "";

            const subtitleNode = hasSubtitle ? (
              <MUITypography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                }}
              >
                {subtitleText}
              </MUITypography>
            ) : null;

            return (
              <>
                <MUIBox
                  sx={{
                    display: "flex",
                    flex: "1 1 auto",
                  }}
                >
                  <MUIBox
                    sx={{
                      flex: "1 1 0",
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <MUIBox sx={{ flex: "1 1 auto" }}>
                      {renderContent(widgetRenderProperties)}
                    </MUIBox>

                    {hasVisual && hasSubtitle && (
                      <MUIBox sx={{ mt: "auto" }}>{subtitleNode}</MUIBox>
                    )}
                  </MUIBox>

                  {hasVisual && (
                    <MUIBox
                      sx={{
                        flex: "0 0 44%",
                        flexShrink: 0,
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {renderedVisual}
                    </MUIBox>
                  )}
                </MUIBox>

                {!hasVisual && hasSubtitle && <MUIBox>{subtitleNode}</MUIBox>}
              </>
            );
          })()}
        </>
      )}
    </MUIPaper>
  );

  if (
    isErrorWidgetData !== true &&
    renderTooltip(widgetRenderProperties) !== null &&
    renderTooltip(widgetRenderProperties) !== undefined &&
    renderTooltip(widgetRenderProperties) !== false
  ) {
    return (
      <MUITooltip
        placement="bottom"
        title={renderTooltip(widgetRenderProperties)}
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: "transparent",
            },
          },
        }}
      >
        {widgetPaperElement}
      </MUITooltip>
    );
  }

  return widgetPaperElement;
}
